import { devLog } from '../../../../utils/devLog';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, User, Phone, Mail, Shield, AlertTriangle, Plus, Loader2 } from 'lucide-react';
import { useVessel } from 'contexts/VesselContext';

// Import form sections from the modal
import PersonalInfoSection from '../../crew/modals/add-crew/PersonalInfoSection';
// FT-28: MedicalSection removed - medical certs now handled in CertificatesSection
import EmergencyContactSection from '../../crew/modals/add-crew/EmergencyContactSection';
import CertificatesSection from '../../crew/modals/add-crew/CertificatesSection';
import NotesSection from '../../crew/modals/add-crew/NotesSection';

// Import helpers
import { getRequiredCertificates, isCertificateRequirementMet } from '../../crew/modals/add-crew/certificateHelpers';
import { validateEmail, findDuplicateCrew } from '../../../../utils/crew/validateCrew';

// FT-449: reusable unsaved-changes guard
import { useUnsavedChangesGuard } from '../../../../hooks/useUnsavedChangesGuard';
import { UnsavedChangesDialog } from '../../common/UnsavedChangesDialog';

// ---------------------------------------------------------------------------
// FT-449: Build the "clean" baseline from initialData (or the empty defaults
// for Add mode). This is the state the form is in when it first opens; dirty
// is only true when the user has changed something since then.
// ---------------------------------------------------------------------------
function buildCleanBaseline(initialData) {
  if (initialData) {
    return {
      id: initialData.id || null,
      name: initialData.name || initialData.full_name || '',
      identification: initialData.identification || '',
      position: initialData.position || '',
      phone: initialData.phone || '',
      email: initialData.email || '',
      start_date: initialData.start_date || new Date().toISOString().split('T')[0],
      medical_type: initialData.medical_type || '',
      medical_expiry: initialData.medical_expiry || '',
      emergency_contact: initialData.emergency_contact || '',
      emergency_phone: initialData.emergency_phone || '',
      notes: initialData.notes || '',
      horRegime: 'seagoing',
      certificates: Array.isArray(initialData.certificates) ? initialData.certificates : [],
    };
  }
  return {
    id: null,
    name: '',
    identification: '',
    position: '',
    phone: '',
    email: '',
    start_date: new Date().toISOString().split('T')[0],
    medical_type: '',
    medical_expiry: '',
    emergency_contact: '',
    emergency_phone: '',
    notes: '',
    horRegime: 'seagoing',
    certificates: [],
  };
}

/** Stable JSON fingerprint of the fields we care about for dirty detection. */
function fingerprint(data) {
  // We compare all user-editable fields. 'id' and 'horRegime' are fixed so
  // they are intentionally included — changing them would also be a real edit.
  return JSON.stringify({
    id: data.id,
    name: data.name,
    identification: data.identification,
    position: data.position,
    phone: data.phone,
    email: data.email,
    start_date: data.start_date,
    medical_type: data.medical_type,
    medical_expiry: data.medical_expiry,
    emergency_contact: data.emergency_contact,
    emergency_phone: data.emergency_phone,
    notes: data.notes,
    horRegime: data.horRegime,
    // Certificates: compare by identity (order-sensitive). Adding, editing or
    // removing a certificate makes this JSON differ from the baseline.
    certificates: data.certificates,
  });
}

const AddCrewView = ({ onBack, onSave, initialData = null, existingCrew = [] }) => {
  const { currentVesselId, getCurrentVessel } = useVessel()
  const currentVessel = getCurrentVessel();

  // ── FT-449: dirty tracking ────────────────────────────────────────────────
  // baselineRef holds the fingerprint of the form at open time (or after save).
  // We compare current formData against it to know if there are unsaved changes.
  const baselineRef = useRef(fingerprint(buildCleanBaseline(initialData)));
  const [isDirty, setIsDirty] = useState(false);

  const { showLeavePrompt, dialogProps } = useUnsavedChangesGuard(isDirty);

  // ─────────────────────────────────────────────────────────────────────────

  const [formData, setFormData] = useState(() => buildCleanBaseline(initialData));

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Populate form when editing existing crew
  useEffect(() => {
    if (initialData) {
      // Edit mode: populate with existing crew data
      const clean = buildCleanBaseline(initialData);
      setFormData(clean);
      // Reset baseline + dirty flag when initialData changes (e.g. re-opening for
      // a different crew member without unmounting the component).
      baselineRef.current = fingerprint(clean);
      setIsDirty(false);
    }
  }, [initialData]);

  // Get vessel area category from vessel config
  const vesselAreaCategory = currentVessel?.category || 3; // Default to Cat 3 if not set
  const vesselEnginePower = currentVessel?.enginePower || 'under1500';
  // FT-29: Use lengthOverall (canonical field name from vesselConfig schema)
  const vesselLength = currentVessel?.lengthOverall || currentVessel?.length || 0;
  // FT-30: Extract equipment config for conditional cert requirements
  // FT-30 Fix: Include navigationEquipment array for user-selected equipment detection
  const vesselEquipment = {
    hasRadar: currentVessel?.hasRadar || currentVessel?.optionalEquipment?.radar,
    hasECDIS: currentVessel?.hasECDIS || currentVessel?.navcomms?.hasECDIS,
    hasCooking: currentVessel?.hasCooking || currentVessel?.accommodation?.hasCooking,
    hasGalley: currentVessel?.hasGalley,
    operations: currentVessel?.operations?.operations || currentVessel?.operations || [],
    navigationEquipment: currentVessel?.navigationEquipment || [],
    accommodation: currentVessel?.accommodation,
    hasStabilityBooklet: currentVessel?.hasStabilityBooklet
  };

  // FT-449: wrap setFormData to keep isDirty accurate after every field edit.
  // useCallback so CertificatesSection's setFormData reference stays stable.
  const setFormDataWithDirty = useCallback((updater) => {
    setFormData((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      setIsDirty(fingerprint(next) !== baselineRef.current);
      return next;
    });
  }, []);

  const handleInputChange = (field, value) => {
    setFormDataWithDirty(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields - only basic information
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.position) newErrors.position = 'Position is required';

    // Email validation (optional field, but must be valid if provided)
    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address (e.g., name@example.com)';
    }

    // Duplicate crew detection (only for new crew, not edit mode)
    if (!initialData && formData.name) {
      const duplicate = findDuplicateCrew(formData.name, existingCrew);
      if (duplicate) {
        newErrors.name = `A crew member named "${duplicate.full_name || duplicate.name}" already exists. Please use a different name or check existing crew.`;
      }
    }

    // Medical expiry is optional - user can add later
    // Certificates are optional - user can add later

    // Note: We still calculate requirements for display purposes,
    // but don't block saving if certificates are missing
    const requirements = getRequiredCertificates(formData.position, vesselAreaCategory, vesselEnginePower, vesselLength, vesselEquipment);
    
    // Check for missing certificates but DON'T add to errors
    // This allows the form to save while we can still show warnings
    const missingMandatory = requirements.mandatoryForAll.certificates
      .filter(cert => cert.type !== 'medical' && !isCertificateRequirementMet(cert, formData))
      .map(cert => cert.label);
    
    // Check position-specific requirements
    let missingPositionCerts = [];
    if (formData.position && requirements.positionSpecific.certificates.length > 0) {
      missingPositionCerts = requirements.positionSpecific.certificates
        .filter(cert => cert.required && !isCertificateRequirementMet(cert, formData))
        .map(cert => cert.label);
    }
    
    // Log missing certificates for info but don't block save
    if (missingMandatory.length > 0 || missingPositionCerts.length > 0) {
      devLog('Missing certificates (not blocking):', [...missingMandatory, ...missingPositionCerts]);
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // FT-449: guarded back/cancel — shows "unsaved changes?" dialog when dirty.
  const handleBack = useCallback(() => {
    showLeavePrompt(onBack);
  }, [showLeavePrompt, onBack]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Submit the form data with loading state
    setIsSaving(true);
    try {
      const result = await onSave(formData);
      // FT-449: mark clean ONLY on a truthy return (null = save failed; CrewModule
      // shows an error toast and returns null on permission errors or network failures).
      // If the save failed, keep isDirty=true so the unsaved-changes guard still fires
      // when the user tries to cancel/navigate away without retrying.
      if (result) {
        baselineRef.current = fingerprint(formData);
        setIsDirty(false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* FT-449: unsaved-changes dialog — rendered here, invisible until dirty + leaving */}
      <UnsavedChangesDialog {...dialogProps} />

      {/* Compact Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                {initialData ? 'Edit Crew Member' : 'Add Crew Member'}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {/* FT-83: pb-24 md:pb-0 prevents Save/Cancel buttons from being hidden behind mobile bottom nav */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 pb-24 md:pb-6">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Personal Information Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="bg-slate-700 px-5 py-3">
                <h3 className="text-base font-semibold text-white flex items-center">
                  <User className="h-5 w-5 mr-2 text-white" />
                  Personal Information
                </h3>
              </div>
              <div className="px-6 py-5">
                <PersonalInfoSection
                  formData={formData}
                  errors={errors}
                  currentVessel={currentVessel}
                  handleInputChange={handleInputChange}
                />
              </div>
            </div>

            {/* FT-28: Medical Information card removed - ENG1/ML5 handled via Certificates section */}

            {/* Emergency Contact Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="bg-slate-700 px-5 py-3">
                <h3 className="text-base font-semibold text-white flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-white" />
                  Emergency Contact
                </h3>
              </div>
              <div className="px-6 py-5">
                <EmergencyContactSection
                  formData={formData}
                  handleInputChange={handleInputChange}
                />
              </div>
            </div>

            {/* Certificates Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="bg-slate-700 px-5 py-3">
                <h3 className="text-base font-semibold text-white flex items-center">
                  <Shield className="h-5 w-5 mr-2 text-white" />
                  Certificates & Qualifications
                </h3>
              </div>
              <div className="px-6 py-5">
                <CertificatesSection
                  formData={formData}
                  errors={errors}
                  position={formData.position}
                  vesselAreaCategory={vesselAreaCategory}
                  vesselEnginePower={vesselEnginePower}
                  vesselLength={vesselLength}
                  vesselEquipment={vesselEquipment}
                  handleInputChange={handleInputChange}
                  setFormData={setFormDataWithDirty}
                />
              </div>
            </div>

            {/* Notes Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="bg-slate-700 px-5 py-3">
                <h3 className="text-base font-semibold text-white">Additional Notes</h3>
              </div>
              <div className="px-6 py-5">
                <NotesSection
                  formData={formData}
                  handleInputChange={handleInputChange}
                />
              </div>
            </div>

            {/* Form Actions - Fixed at bottom */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 px-6 py-5">
              <div className="flex items-center justify-end gap-3">
                {/* FT-16: min-h-[44px] ensures 44px+ touch targets on mobile */}
                {/* FT-449: guarded cancel — shows "unsaved changes?" when dirty */}
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-4 py-3 md:py-2 text-base md:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 min-h-[44px]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-4 py-3 md:py-2 text-base md:text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-h-[44px]"
                >
                  {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isSaving
                    ? 'Saving...'
                    : initialData ? 'Update Crew Member' : 'Save Crew Member'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCrewView;